import { isAllowed } from "@helpers/checkPermission";
import {
  getModuleDefaultPathsStorageKey,
  getCurrentModuleDefaultUserId,
  normalizeModulePath,
  MODULE_DEFAULT_PATH_CHANGED_EVENT,
} from "@helpers/moduleDefaultPath";

/**
 * 可設定「點擊該分組時預設進哪一頁」的側欄群組。
 * storage 與區塊首頁共用 moduleDefaultPaths:${userId}，key 為 `${routerName}/${groupEng}`。
 */
export const GROUP_DEFAULT_HOME_CONFIGS = [
  {
    key: "options/medical-record",
    routerName: "options",
    groupEng: "medical-record",
    title: "病歷管理",
    description: "點擊設定側欄「病歷管理」時優先前往的子頁。",
    paths: [
      "/options/canned-message",
      "/options/treatment-record-templates",
    ],
    pathLabels: {
      "/options/canned-message": "罐頭訊息",
      "/options/treatment-record-templates": "療程紀錄模板",
    },
  },
  {
    key: "shiftschedule/service",
    routerName: "shiftschedule",
    groupEng: "service",
    title: "服務設定",
    description: "點擊班表側欄「服務設定」時優先前往的子頁。",
    paths: [
      "/shiftschedule/service",
      "/shiftschedule/categorymanagement",
      "/shiftschedule/service-cost",
    ],
    pathLabels: {
      "/shiftschedule/service": "服務上架管理",
      "/shiftschedule/categorymanagement": "分類管理",
      "/shiftschedule/service-cost": "成本與分潤",
    },
  },
  {
    key: "shiftschedule/consent-templates",
    routerName: "shiftschedule",
    groupEng: "consent-templates",
    title: "術前同意書",
    description: "點擊班表側欄「術前同意書」時優先前往的子頁。",
    paths: [
      "/shiftschedule/consent-templates",
      "/shiftschedule/consent-category-link",
      "/shiftschedule/consent-service-link",
    ],
    pathLabels: {
      "/shiftschedule/consent-templates": "範本設定",
      "/shiftschedule/consent-category-link": "對應服務分類",
      "/shiftschedule/consent-service-link": "服務關聯設定",
    },
  },
  {
    key: "shiftschedule/payment-consent-template",
    routerName: "shiftschedule",
    groupEng: "payment-consent-template",
    title: "付款同意書",
    description: "點擊班表側欄「付款同意書」時優先前往的子頁。",
    paths: [
      "/shiftschedule/payment-consent-template",
      "/shiftschedule/payment-consent-category-link",
      "/shiftschedule/payment-consent-service-link",
    ],
    pathLabels: {
      "/shiftschedule/payment-consent-template": "範本設定",
      "/shiftschedule/payment-consent-category-link": "對應服務分類",
      "/shiftschedule/payment-consent-service-link": "服務關聯設定",
    },
  },
  {
    key: "shiftschedule/questionnaire-templates",
    routerName: "shiftschedule",
    groupEng: "questionnaire-templates",
    title: "問卷單",
    description: "點擊班表側欄「問卷單」時優先前往的子頁。",
    paths: [
      "/shiftschedule/questionnaire-templates",
      "/shiftschedule/questionnaire-category-link",
      "/shiftschedule/questionnaire-service-link",
    ],
    pathLabels: {
      "/shiftschedule/questionnaire-templates": "範本設定",
      "/shiftschedule/questionnaire-category-link": "對應服務分類",
      "/shiftschedule/questionnaire-service-link": "服務關聯設定",
    },
  },
];

const GROUP_CONFIG_BY_KEY = Object.fromEntries(
  GROUP_DEFAULT_HOME_CONFIGS.map((config) => [config.key, config])
);

export function getGroupDefaultHomeKey(routerName, groupEng) {
  if (!routerName || !groupEng) return null;
  return `${routerName}/${groupEng}`;
}

export function getGroupDefaultHomeConfig(routerName, groupEng) {
  const key = getGroupDefaultHomeKey(routerName, groupEng);
  return key ? GROUP_CONFIG_BY_KEY[key] || null : null;
}

function readAllSavedPaths(userId = getCurrentModuleDefaultUserId()) {
  if (typeof window === "undefined") return {};
  const key = getModuleDefaultPathsStorageKey(userId);
  if (!key) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (err) {
    console.log("Error parsing group default paths", err);
    return {};
  }
}

function writeAllSavedPaths(paths, userId = getCurrentModuleDefaultUserId()) {
  if (typeof window === "undefined") return;
  const key = getModuleDefaultPathsStorageKey(userId);
  if (!key) return;
  if (Object.keys(paths).length === 0) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(paths));
}

export function getSavedGroupDefaultPath(
  routerName,
  groupEng,
  userId = getCurrentModuleDefaultUserId()
) {
  const key = getGroupDefaultHomeKey(routerName, groupEng);
  if (!key) return null;
  const saved = readAllSavedPaths(userId)[key];
  return saved ? normalizeModulePath(saved) : null;
}

export function hasUserConfiguredGroupDefault(
  routerName,
  groupEng,
  userId = getCurrentModuleDefaultUserId()
) {
  return Boolean(getSavedGroupDefaultPath(routerName, groupEng, userId));
}

export function persistGroupDefaultPath(
  routerName,
  groupEng,
  path,
  userId = getCurrentModuleDefaultUserId()
) {
  if (typeof window === "undefined" || !routerName || !groupEng || !path) return;
  const key = getGroupDefaultHomeKey(routerName, groupEng);
  if (!key) return;

  const paths = readAllSavedPaths(userId);
  paths[key] = normalizeModulePath(path);
  writeAllSavedPaths(paths, userId);

  window.dispatchEvent(
    new CustomEvent(MODULE_DEFAULT_PATH_CHANGED_EVENT, {
      detail: { sidebarName: key, path: paths[key], userId, kind: "group" },
    })
  );
}

export function clearGroupDefaultPath(
  routerName,
  groupEng,
  userId = getCurrentModuleDefaultUserId()
) {
  if (typeof window === "undefined" || !routerName || !groupEng) return;
  const key = getGroupDefaultHomeKey(routerName, groupEng);
  if (!key) return;

  const paths = readAllSavedPaths(userId);
  delete paths[key];
  writeAllSavedPaths(paths, userId);

  window.dispatchEvent(
    new CustomEvent(MODULE_DEFAULT_PATH_CHANGED_EVENT, {
      detail: { sidebarName: key, path: null, userId, kind: "group" },
    })
  );
}

export function getFirstAccessibleGroupPath(config, permissions) {
  if (!config?.paths?.length) return null;
  for (const path of config.paths) {
    const normalized = normalizeModulePath(path);
    if (normalized && isAllowed(normalized, permissions)) {
      return normalized;
    }
  }
  return null;
}

/**
 * 解析群組點擊時應前往的 path：
 * 有自訂且仍有權限 → 用自訂；否則依設定順序找第一個有權限頁。
 * 若自訂已無權限，會重寫為 fallback。
 */
export function resolveGroupDefaultPathForNavigation(
  routerName,
  groupEng,
  permissions,
  userId = getCurrentModuleDefaultUserId()
) {
  const config = getGroupDefaultHomeConfig(routerName, groupEng);
  if (!config) {
    return { path: null, eng: null, didReset: false };
  }

  const fallback = getFirstAccessibleGroupPath(config, permissions);
  if (!fallback) {
    return { path: null, eng: null, didReset: false };
  }

  const savedPath = getSavedGroupDefaultPath(routerName, groupEng, userId);
  if (savedPath && isAllowed(savedPath, permissions)) {
    return {
      path: savedPath,
      eng: pathToEng(savedPath, routerName),
      didReset: false,
    };
  }

  if (savedPath) {
    persistGroupDefaultPath(routerName, groupEng, fallback, userId);
    return {
      path: fallback,
      eng: pathToEng(fallback, routerName),
      didReset: true,
    };
  }

  return {
    path: fallback,
    eng: pathToEng(fallback, routerName),
    didReset: false,
  };
}

export function pathToEng(path, routerName) {
  const normalized = normalizeModulePath(path);
  if (!normalized || !routerName) return null;
  const prefix = `/${routerName}/`;
  if (!normalized.startsWith(prefix)) return null;
  return normalized.slice(prefix.length) || null;
}

export function getGroupDefaultHomeDialogTitle(routerName, groupEng) {
  const config = getGroupDefaultHomeConfig(routerName, groupEng);
  if (!config) return "設定分組首頁";
  return `設定「${config.title}」預設頁`;
}
